import type { ReactNode } from 'react';
import { Banknote, HandCoins, ImageIcon, MessageSquareText, type LucideIcon } from 'lucide-react';
import { DetailValue } from '@/components/common/DetailValue';
import { FormList, FormListItem } from '@/components/common/FormDialog';
import { cn } from '@/lib/utils';
import { STICKY_CARD_HEADER_CLASS } from '@/styles/stickyCardHeader';
import type { MpApprovalDetail } from '../types/marketPromotion.types';
import { CompactSectionTitle } from './CompactSectionTitle';
import { DonationImageFields } from './DonationImageFields';
import {
  DECISION_ROWS,
  DONATION_ROWS,
  PAYMENT_ROWS,
  ZONAL_MANAGER_COMMENT_ROWS,
  type DonationRow,
} from './donationRows';

interface DonationRowsProps {
  donation: MpApprovalDetail | undefined;
  rows: readonly DonationRow[];
}

export function DonationRows({ donation, rows }: DonationRowsProps) {
  return rows.map((row) => (
    <FormListItem key={row.label} label={row.label}>
      <DetailValue isLoading={donation === undefined}>
        {donation ? row.value(donation) : null}
      </DetailValue>
    </FormListItem>
  ));
}

interface DonationCardSectionProps {
  title: string;
  icon: LucideIcon;
  children: ReactNode;
}

function DonationCardSection({ title, icon, children }: DonationCardSectionProps) {
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <CompactSectionTitle title={title} icon={icon} appearance="bar" />
      {children}
    </section>
  );
}

interface DonationDetailsCardHeader {
  title: string;
  icon: LucideIcon;
  actions: ReactNode;
}

interface DonationDetailsCardProps {
  donation: MpApprovalDetail | undefined;
  header?: DonationDetailsCardHeader | undefined;
  paymentRows?: ReactNode;
  decisionRows?: ReactNode;
  hasPictureFields?: boolean | undefined;
}

export function DonationDetailsCard({
  donation,
  header,
  paymentRows,
  decisionRows,
  hasPictureFields = false,
}: DonationDetailsCardProps) {
  return (
    <section
      aria-label={header?.title ?? 'Donation details'}
      className="flex flex-col rounded-2xl bg-card px-4 pb-5 shadow-sm ring-1 ring-primary/10"
    >
      {header ? (
        <div
          className={cn(
            'flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-border pt-1.5 pb-2',
            STICKY_CARD_HEADER_CLASS,
          )}
        >
          <CompactSectionTitle title={header.title} icon={header.icon} />
          <div className="flex flex-wrap items-center gap-2">{header.actions}</div>
        </div>
      ) : null}

      <div className="flex flex-col gap-6 pt-4">
        <DonationCardSection title="Donation" icon={HandCoins}>
          <FormList columnCount={2}>
            <DonationRows donation={donation} rows={DONATION_ROWS} />
          </FormList>
        </DonationCardSection>

        <DonationCardSection title="Payment" icon={Banknote}>
          <FormList columnCount={2}>
            {paymentRows ?? <DonationRows donation={donation} rows={PAYMENT_ROWS} />}
          </FormList>
        </DonationCardSection>

        <DonationCardSection title="Comments" icon={MessageSquareText}>
          <FormList>
            <DonationRows donation={donation} rows={ZONAL_MANAGER_COMMENT_ROWS} />
            {decisionRows ?? <DonationRows donation={donation} rows={DECISION_ROWS} />}
          </FormList>
        </DonationCardSection>

        {hasPictureFields ? (
          <DonationCardSection title="Pictures" icon={ImageIcon}>
            <DonationImageFields />
          </DonationCardSection>
        ) : null}
      </div>
    </section>
  );
}
